/**
 * ストリートビュー机上調査 結果地図 (docs/25 Increment 2)。
 *
 * `apps/admin/src/features/posts-moderation/ModerationMap.tsx` の MapLibre 初期化・
 * マーカー差分更新パターンを土台に、以下を追加:
 *   - マーカー色は色分けの軸 (調査項目) で決める。軸が無ければ単色 (地点のスコアでは塗らない、2026-09-16)
 *   - 選択中マーカーの強調表示
 *   - クリックで頂点を追加していく多角形描画によるエリア選択 (`lassoEnabled` 時のみ有効。
 *     2026-07-15 にフリーハンド投げ縄から変更 — 細かい範囲を正確になぞりにくいとの声を受け、
 *     一括解析の線描画モードと同じクリック頂点追加方式に統一)
 *   - 一括解析向けの線描画 (`lineDrawEnabled` 時のみ有効)
 */
import { Button } from '@citizen-walk/shared-ui';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type Supercluster from 'supercluster';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import {
  categoryRowKey,
  resolveCategoryDisplay,
  resolveMapLegendAxis,
  resolvePostCategoryCounts,
} from './streetSurveyPinDisplay';
import {
  buildClusterIndex,
  buildDonutSvgMarkup,
  isClusterFeature,
  resolveDonutSegments,
  type ClusterAggProps,
} from './streetSurveyClusterDonut';
import { resolveChipColor, resolveChipEmoji } from '../posts-moderation';
/** 色分けの軸が無いときの解析済み地点の色 (スコアで塗らない、2026-09-16)。 */
const ANALYZED_PLAIN_COLOR = '#475569';

const CLUSTER_FALLBACK_COLOR = '#3B82F6';
/** クラスタクリック時のズームイン上限 (地図全体のタイル解像度上限に合わせる)。 */
const CLUSTER_MAX_ZOOM_FOR_EXPANSION = 18;

const LASSO_SOURCE_ID = 'street-survey-lasso-src';
const LASSO_LAYER_ID = 'street-survey-lasso-fill';

const LINE_DRAW_SOURCE_ID = 'street-survey-line-draw-src';
const LINE_DRAW_LAYER_ID = 'street-survey-line-draw-line';

const ROUTE_DRAW_SOURCE_ID = 'street-survey-route-draw-src';
const ROUTE_DRAW_LAYER_ID = 'street-survey-route-draw-line';

/** ray casting (奇偶判定) で点が閉曲線 ring 内にあるか。 */
function pointInRing(pt: [number, number], ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    if (!pi || !pj) continue;
    const [xi, yi] = pi;
    const [xj, yj] = pj;
    const intersect =
      yi > pt[1] !== yj > pt[1] && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export interface StreetSurveyResultsMapProps {
  /**
   * 表示中の地区。地区を切り替えても本コンポーネントは再マウントされない
   * (`/districts/:districtId/street-survey` の param 変更のみ) ため、初回 fitBounds を
   * やり直す判定に使う (2026-07-16)。
   */
  districtId?: string;
  posts: StreetSurveyPost[];
  selectedPostId: string | null;
  onSelectPost: (id: string) => void;
  lassoEnabled: boolean;
  /** 選んだ地点IDに加え、選択に使った多角形の頂点も渡す (2026-07-16、エリア集計PDFの地図用)。 */
  onLassoSelect: (postIds: string[], polygon: { latitude: number; longitude: number }[]) => void;
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  /** 新規地点解析の「地図をクリックして選ぶ」モード (docs/25、地図統合)。 */
  pickMode?: boolean;
  /** pickMode 中に選んだ未解析の候補座標。専用マーカーで表示する。 */
  pendingLocation?: { latitude: number; longitude: number } | null;
  onPickLocation?: (loc: { latitude: number; longitude: number }) => void;
  /**
   * 一括解析の「地図で線を描く」モード (docs/25 §7-4 拡張、2026-07-15)。クリックで頂点を
   * 追加していき、ダブルクリックまたは地図下部の「完了」ボタンで確定する。
   */
  lineDrawEnabled?: boolean;
  onLineDrawn?: (coords: { latitude: number; longitude: number }[]) => void;
  /**
   * 「既存地点から路線を作成」モード (docs/25 §10-6 拡張、2026-07-21)。線描画のUI自体は
   * `lineDrawEnabled` と全く同じ (クリックで頂点追加、ダブルクリック/完了ボタンで確定)。
   * 完了時に新規AI解析は行わず、描いた線の座標だけを呼出元に渡す (既存点の近接判定は
   * `RouteReportModal.tsx` 側の責務)。
   */
  routeDrawEnabled?: boolean;
  onRouteDrawn?: (coords: { latitude: number; longitude: number }[]) => void;
  /** 投稿が1件も無い地区で地図を開いた際の初期中心 (地区の投稿可能エリア中心)。 */
  defaultCenter?: { latitude: number; longitude: number };
  /**
   * 解析対象として複数選択中の地点 (docs/27 §7、登録済み 360度写真の選択)。選択中はオレンジの
   * リングで示す。未解析の地点 (analysisStatus!=='done') はドーナツではなく灰色のリング + 360 で描く。
   */
  multiSelectedIds?: ReadonlySet<string>;
  /**
   * 全画面表示中のみ地図上に重ねて表示する内容 (2026-07-16)。`StreetSurveyActionBar` 等、
   * 通常は地図の外側 (= 全画面表示の対象外) に配置されているパネルを渡す。全画面表示中は
   * 外側の要素が一切見えなくなる (ブラウザの Fullscreen API は対象要素の子孫しか描画しない)
   * ため、同じ内容をここにも渡して地図の内側からも操作できるようにする。
   */
  children?: ReactNode;
}

export function StreetSurveyResultsMap({
  districtId,
  posts,
  selectedPostId,
  onSelectPost,
  lassoEnabled,
  onLassoSelect,
  primaryAxis,
  roleTypeAxis,
  pickMode = false,
  pendingLocation = null,
  onPickLocation,
  lineDrawEnabled = false,
  onLineDrawn,
  routeDrawEnabled = false,
  onRouteDrawn,
  defaultCenter,
  multiSelectedIds,
  children,
}: StreetSurveyResultsMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // 全画面表示の対象 (地図本体だけでなく凡例・線描画コントロール等の兄弟要素も含めるため、
  // 地図 canvas の親ではなく一番外側の wrapper を対象にする、2026-07-16)。
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // 全画面表示中かどうか (2026-07-16、`children` オーバーレイの表示条件用)。
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 凡例の開閉状態 (docs/25c、2026-07-15)。既定は開いた状態 (従来どおり)。地図上の凡例が
  // マーカーの一部を覆い隠すことがあるため、必要な時だけ閉じられるようにする。
  const [legendOpen, setLegendOpen] = useState(true);
  // 線描画モードの頂点数 (地図下部の浮動「完了」コントロール用、docs/25 §7-4 拡張)。
  // 頂点配列自体はクロージャローカル (下の useEffect 内 `path` 参照)。
  const [drawVertexCount, setDrawVertexCount] = useState(0);
  const finishLineDrawRef = useRef<(() => void) | null>(null);
  // 「既存地点から路線を作成」モードの頂点数 (線描画と同じパターン、2026-07-21)。
  const [routeDrawVertexCount, setRouteDrawVertexCount] = useState(0);
  const finishRouteDrawRef = useRef<(() => void) | null>(null);
  // エリア選択 (多角形描画) モードの頂点数、線描画と同じパターン (2026-07-15、投げ縄から
  // クリック頂点追加方式へ変更)。
  const [areaVertexCount, setAreaVertexCount] = useState(0);
  const finishAreaDrawRef = useRef<(() => void) | null>(null);
  // 未クラスタの投稿単体マーカー (postId キー、既存の diff 更新パターンを維持しちらつきを防ぐ)。
  const markersRef = useRef<
    Map<string, { marker: maplibregl.Marker; display: string; selected: boolean }>
  >(new Map());
  // クラスタマーカー (ズーム/移動のたびに構成が変わるため毎回再生成する)。
  const clusterMarkersRef = useRef<maplibregl.Marker[]>([]);
  // 新規地点解析の候補座標マーカー (最大1個、pickMode 中のみ表示)。
  const pendingMarkerRef = useRef<maplibregl.Marker | null>(null);
  const postsRef = useRef(posts);
  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);
  const onSelectRef = useRef(onSelectPost);
  useEffect(() => {
    onSelectRef.current = onSelectPost;
  }, [onSelectPost]);
  const onLassoSelectRef = useRef(onLassoSelect);
  useEffect(() => {
    onLassoSelectRef.current = onLassoSelect;
  }, [onLassoSelect]);
  const onPickLocationRef = useRef(onPickLocation);
  useEffect(() => {
    onPickLocationRef.current = onPickLocation;
  }, [onPickLocation]);
  const onLineDrawnRef = useRef(onLineDrawn);
  useEffect(() => {
    onLineDrawnRef.current = onLineDrawn;
  }, [onLineDrawn]);
  const onRouteDrawnRef = useRef(onRouteDrawn);
  useEffect(() => {
    onRouteDrawnRef.current = onRouteDrawn;
  }, [onRouteDrawn]);

  // 地図の色分け・凡例に使う軸 (role_type があれば優先、無ければ primary 軸にフォールバック、
  // docs/25c 2026-07-14)。primary 軸は M3 で必須項目のため実質ほぼ常に非 null。
  // useMemo で参照を安定させ、下の useEffect の diff 更新 (ちらつき防止) を壊さないようにする。
  const legendAxis = useMemo(
    () => resolveMapLegendAxis(primaryAxis, roleTypeAxis),
    [primaryAxis, roleTypeAxis],
  );

  // 地図の初期化
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: defaultCenter ? [defaultCenter.longitude, defaultCenter.latitude] : [139.767, 35.681],
      zoom: 12,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    // 近接する地点 (ドーナツクラスタ) を見分けるには相当ズームしないと難しいため、
    // ブラウザ全画面表示で大きく見られるようにする (2026-07-16、MapLibre 標準コントロール)。
    // container を一番外側の wrapper にすることで、凡例・線描画コントロール等 (地図 canvas の
    // 兄弟要素、absolute 配置) も全画面表示に含める (既定のままだと地図 canvas だけが全画面化
    // され、それらの UI が消えてしまう)。
    map.addControl(
      new maplibregl.FullscreenControl({ container: wrapperRef.current ?? undefined }),
      'top-right',
    );
    // 縮尺バー (2026-07-16 追加)。凡例 (bottom-left) や線描画/エリア選択の頂点数コントロール
    // (bottom-center) と重ならない bottom-right に置く。
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 100, unit: 'metric' }), 'bottom-right');
    map.on('load', () => map.resize());
    mapRef.current = map;
    const markers = markersRef.current;

    // 地図の高さは右側パネル (座標選択カード/画像ボード/パノラマ等) の高さに追従して変わる
    // (2026-07-16、グリッドの stretch で右パネルと同じ高さにする修正)。MapLibre は container の
    // 要素サイズ変化を自動検知しないため、ResizeObserver で明示的に resize() する。
    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      markers.clear();
    };
    // defaultCenter は初期化時の一度きりの中心指定 (以降の変化では地図を動かさない)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 全画面表示の on/off を検知する (`children` オーバーレイの表示条件、2026-07-16)。
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // クラスタ index は posts/legendAxis が変わるたび再構築 (軽量、supercluster.load は同期)。
  const clusterIndexRef = useRef(buildClusterIndex(posts, legendAxis?.axis));
  useEffect(() => {
    clusterIndexRef.current = buildClusterIndex(posts, legendAxis?.axis);
  }, [posts, legendAxis]);

  // 初回に地点が揃った時点で1度だけ全体表示に合わせる (実際の fitBounds は下のマーカー
  // 描画 effect 内、地点が1件以上ある時のみ)。
  //
  // リセット条件が `[posts]` だと、`posts` は onSnapshot のたびに新しい配列になるため
  // **解析結果が1件書き込まれるたびに再 fit が走り、ユーザーのパン/ズームを奪っていた**
  // (一括解析中は50点分連続で発生する)。地区切替時のみやり直す (2026-07-16 修正)。
  const hasFitBoundsRef = useRef(false);
  useEffect(() => {
    hasFitBoundsRef.current = false;
  }, [districtId]);

  // マーカー描画 (投稿単体は差分更新でちらつき防止、クラスタはズーム/移動のたび作り直す、docs/25)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const render = () => {
      const prev = markersRef.current;

      // 表示キー: 色分け軸 (role_type、無ければ primary 軸) があれば地点内の検出要素の
      // カテゴリ別内訳 (ドーナツ)、どちらも無ければ単色 (地点のスコアでは塗らない、2026-09-16)。
      const displayKeyOf = (p: StreetSurveyPost): string => {
        const multi = multiSelectedIds?.has(p.id) ? 'm1:' : 'm0:';
        if (p.analysisStatus !== 'done') return `${multi}unanalyzed:${p.analysisStatus}`;
        if (legendAxis) {
          const counts = resolvePostCategoryCounts(p, legendAxis.axis);
          const sorted = Object.entries(counts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([cd, n]) => `${cd}:${n}`)
            .join(',');
          return `${multi}donut:${sorted}`;
        }
        return `${multi}plain`;
      };

      // 現在のビューポートをクラスタリングし、クラスタ/未クラスタ点を仕分ける。
      const b = map.getBounds();
      const bbox: [number, number, number, number] = [
        b.getWest(),
        b.getSouth(),
        b.getEast(),
        b.getNorth(),
      ];
      const zoom = Math.round(map.getZoom());
      const features = clusterIndexRef.current.getClusters(bbox, zoom);
      const unclusteredIds = new Set<string>();
      const clusterFeatures: Supercluster.ClusterFeature<ClusterAggProps>[] = [];
      for (const f of features) {
        if (isClusterFeature(f)) {
          clusterFeatures.push(f);
        } else {
          unclusteredIds.add(f.properties.postId);
        }
      }

      // 投稿単体マーカー: ビューポート外に出た/クラスタに吸収された分は削除。
      for (const [id, entry] of prev) {
        const post = posts.find((p) => p.id === id);
        const isSelected = id === selectedPostId;
        if (
          !post ||
          !unclusteredIds.has(id) ||
          displayKeyOf(post) !== entry.display ||
          isSelected !== entry.selected
        ) {
          entry.marker.remove();
          prev.delete(id);
        }
      }
      for (const id of unclusteredIds) {
        if (prev.has(id)) continue;
        const p = posts.find((post) => post.id === id);
        if (!p) continue;
        const isSelected = p.id === selectedPostId;
        // ドーナツは彩度の高い背景地図 (POIアイコン等) に埋もれやすいため、白い縁取り台座
        // (padSize) の上にドーナツを乗せてコントラストを確保する。
        const donutSize = isSelected ? 45 : 36;
        const padSize = donutSize + 9;
        const el = document.createElement('div');
        const isMulti = multiSelectedIds?.has(p.id) === true;
        const selectionRing = isSelected
          ? '0 0 0 3px #0EA5E9, '
          : isMulti
            ? '0 0 0 3px #F59E0B, '
            : '';
        // 注意: position は指定しない。maplibre-gl.css の `.maplibregl-marker{position:absolute}`
        // (JS ではなく CSS クラスで付与される) が、インライン style で position:relative 等を
        // 指定すると詳細度でそちらに上書きされ、マーカーが通常のドキュメントフローに乗って
        // 兄弟マーカーの高さ分ずつ縦積みされてしまう (ズームで位置がずれて見えるバグの原因)。
        // absolute は子要素の position:absolute (絵文字ラベル) の containing block にもなるため、
        // これで十分。
        el.style.cssText = `width:${padSize}px;height:${padSize}px;border-radius:50%;cursor:pointer;background:white;border:1.5px solid rgba(15,23,42,0.2);box-shadow:${selectionRing}0 1px 4px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;`;
        if (p.analysisStatus !== 'done') {
          // 未解析の登録済み 360度写真 (docs/27 §7): 灰色のリング + 「360」。解析すると通常表示になる。
          const ring = document.createElement('div');
          const ringColor = p.analysisStatus === 'failed' ? '#DC2626' : '#64748B';
          ring.style.cssText = `width:${donutSize}px;height:${donutSize}px;border-radius:50%;border:4px ${p.analysisStatus === 'pending' ? 'dotted' : 'dashed'} ${ringColor};box-sizing:border-box;display:flex;align-items:center;justify-content:center;font-size:${Math.max(8, donutSize * 0.28)}px;font-weight:700;color:${ringColor};`;
          ring.textContent = '360';
          el.appendChild(ring);
        } else if (legendAxis) {
          // 地点内の検出要素をカテゴリ別に集計しドーナツ表示 (地点ごとの内訳、docs/25c)。
          const counts = resolvePostCategoryCounts(p, legendAxis.axis);
          const segments = resolveDonutSegments(counts, legendAxis.axis);
          const donutWrap = document.createElement('div');
          donutWrap.style.cssText = `width:${donutSize}px;height:${donutSize}px;`;
          donutWrap.innerHTML = buildDonutSvgMarkup(
            segments,
            donutSize,
            Math.max(4, donutSize * 0.32),
            '#94A3B8',
          );
          el.appendChild(donutWrap);
          const categoryDisplay = resolveCategoryDisplay(p, legendAxis.axis);
          if (categoryDisplay?.emoji) {
            const label = document.createElement('span');
            label.textContent = categoryDisplay.emoji;
            label.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:${donutSize * 0.4}px;line-height:1;`;
            el.appendChild(label);
          }
        } else {
          const dot = document.createElement('div');
          dot.style.cssText = `width:${donutSize}px;height:${donutSize}px;border-radius:50%;background:${ANALYZED_PLAIN_COLOR};`;
          el.appendChild(dot);
        }
        el.title =
          p.analysisStatus !== 'done'
            ? `${p.fileName ?? '360度写真'} (${p.analysisStatus === 'failed' ? '解析失敗' : p.analysisStatus === 'pending' ? '解析中' : '未解析'})`
            : legendAxis
              ? `${legendAxis.label}: ${p.classifications.length}件`
              : `解析済み (${p.classifications.length}件)`;
        el.addEventListener('click', () => onSelectRef.current(p.id));
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([p.location.longitude, p.location.latitude])
          .addTo(map);
        prev.set(p.id, { marker, display: displayKeyOf(p), selected: isSelected });
      }

      // クラスタマーカー: 毎回作り直す (ズーム/移動のたび構成が変わるため、docs/25)。
      clusterMarkersRef.current.forEach((m) => m.remove());
      clusterMarkersRef.current = clusterFeatures.map((f) => {
        const lng = f.geometry.coordinates[0] ?? 0;
        const lat = f.geometry.coordinates[1] ?? 0;
        const pointCount = f.properties.point_count;
        const size = Math.min(56, 28 + Math.sqrt(pointCount) * 7);
        const segments = legendAxis
          ? resolveDonutSegments(f.properties.counts, legendAxis.axis)
          : [];
        const svg = buildDonutSvgMarkup(
          segments,
          size,
          Math.max(4, size * 0.18),
          CLUSTER_FALLBACK_COLOR,
        );
        const el = document.createElement('div');
        // position は指定しない (個別地点マーカーと同じ理由、上記コメント参照)。
        el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4));`;
        el.innerHTML = svg;
        const label = document.createElement('span');
        label.textContent = String(pointCount);
        label.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:${size * 0.32}px;font-weight:700;color:#1e293b;`;
        el.appendChild(label);
        el.addEventListener('click', () => {
          try {
            const expansionZoom = clusterIndexRef.current.getClusterExpansionZoom(
              f.properties.cluster_id,
            );
            map.easeTo({
              center: [lng, lat],
              zoom: Math.min(expansionZoom, CLUSTER_MAX_ZOOM_FOR_EXPANSION),
            });
          } catch {
            /* ignore */
          }
        });
        return new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
      });

      // 初回のみ全投稿の範囲にフィットする (以降はユーザー操作による表示範囲を尊重)。
      if (!hasFitBoundsRef.current) {
        const bounds = new maplibregl.LngLatBounds();
        let hasPoint = false;
        posts.forEach((p) => {
          if (p.location.latitude === 0 && p.location.longitude === 0) return;
          bounds.extend([p.location.longitude, p.location.latitude]);
          hasPoint = true;
        });
        if (hasPoint) {
          hasFitBoundsRef.current = true;
          map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
        }
      }
    };

    render();
    map.on('zoomend', render);
    map.on('moveend', render);
    return () => {
      map.off('zoomend', render);
      map.off('moveend', render);
    };
  }, [posts, selectedPostId, legendAxis, multiSelectedIds]);

  // エリア選択 (投げ縄→多角形描画、2026-07-15 変更)。lassoEnabled 時のみ、pickMode/lineDrawEnabled
  // とは排他。クリックで頂点を追加していき、ダブルクリックまたは地図下部の「確定」ボタン
  // (finishAreaDrawRef 経由) で確定する — 一括解析の線描画モードと同じクリック頂点追加方式
  // (フリーハンドのドラッグでは細かい範囲を正確になぞりにくいとの声を受けて変更)。
  // 多角形の内外判定 (`pointInRing` ray-casting) は `apps/user/src/features/map/MapViewImpl.tsx`
  // (L95-107 / L1471-1616) のロジックを参考に admin 側で新規実装したもの (apps/admin は
  // apps/user を import できないため、ESLint boundaries に従いクロスapp import はしていない)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !lassoEnabled || pickMode || lineDrawEnabled || routeDrawEnabled) return;
    const canvas = (() => {
      try {
        return map.getCanvas();
      } catch {
        return null;
      }
    })();

    let path: [number, number][] = [];
    const emptyFc = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });
    const ensureLayer = () => {
      try {
        if (!map.getSource(LASSO_SOURCE_ID)) {
          map.addSource(LASSO_SOURCE_ID, { type: 'geojson', data: emptyFc() });
          map.addLayer({
            id: LASSO_LAYER_ID,
            type: 'fill',
            source: LASSO_SOURCE_ID,
            paint: {
              'fill-color': '#0EA5E9',
              'fill-opacity': 0.18,
              'fill-outline-color': '#0EA5E9',
            },
          });
        }
      } catch {
        /* silent */
      }
    };
    const setRing = (ring: [number, number][]) => {
      try {
        const src = map.getSource(LASSO_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (!src) return;
        if (ring.length < 3) {
          src.setData(emptyFc());
          return;
        }
        const first = ring[0];
        if (!first) return;
        const closed = [...ring, first];
        src.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: { type: 'Polygon', coordinates: [closed] },
            },
          ],
        });
      } catch {
        /* silent */
      }
    };
    const finish = () => {
      if (path.length < 3) return;
      // ダブルクリックの2回目のクリックも onClick で頂点として追加されるため、直近2点が
      // ごく近接 (2m程度) していれば片方を除去する (線描画モードと同じ処理)。
      const trimmed = [...path];
      if (trimmed.length >= 2) {
        const a = trimmed[trimmed.length - 2];
        const b = trimmed[trimmed.length - 1];
        if (a && b) {
          const dLng = a[0] - b[0];
          const dLat = a[1] - b[1];
          const approxMetersPerDeg = 111_000;
          const distM = Math.hypot(dLng * approxMetersPerDeg, dLat * approxMetersPerDeg);
          if (distM < 2) trimmed.pop();
        }
      }
      if (trimmed.length >= 3) {
        const ids: string[] = [];
        for (const p of postsRef.current) {
          if (p.location.latitude === 0 && p.location.longitude === 0) continue;
          if (pointInRing([p.location.longitude, p.location.latitude], trimmed)) ids.push(p.id);
        }
        // AreaReportModal は地図の全画面表示対象 (wrapperRef) の外に描画されるため、全画面表示
        // 中のままだとエリア集計レポートが (存在はしても) 画面に出てこない。エリア選択完了と
        // 同時に全画面表示を自動解除し、ユーザーが手動で解除しなくてもレポートが見えるようにする
        // (2026-07-16)。
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {
            /* ブラウザ非対応・拒否等は無視 (レポート自体はモーダルとして開くので致命的ではない) */
          });
        }
        onLassoSelectRef.current(
          ids,
          trimmed.map(([lng, lat]) => ({ latitude: lat, longitude: lng })),
        );
      }
      path = [];
      setRing([]);
      setAreaVertexCount(0);
    };
    finishAreaDrawRef.current = finish;

    const onClick = (e: maplibregl.MapMouseEvent) => {
      ensureLayer();
      path.push([e.lngLat.lng, e.lngLat.lat]);
      setRing(path);
      setAreaVertexCount(path.length);
    };
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (path.length === 0) return;
      setRing([...path, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const onDblClick = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault();
      finish();
    };

    map.on('click', onClick);
    map.on('mousemove', onMouseMove);
    map.on('dblclick', onDblClick);
    try {
      map.doubleClickZoom.disable();
    } catch {
      /* */
    }
    if (canvas) canvas.style.cursor = 'crosshair';

    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMouseMove);
      map.off('dblclick', onDblClick);
      finishAreaDrawRef.current = null;
      path = [];
      setAreaVertexCount(0);
      if (canvas) canvas.style.cursor = '';
      try {
        map.doubleClickZoom.enable();
      } catch {
        /* */
      }
      try {
        if (map.getLayer(LASSO_LAYER_ID)) map.removeLayer(LASSO_LAYER_ID);
        if (map.getSource(LASSO_SOURCE_ID)) map.removeSource(LASSO_SOURCE_ID);
      } catch {
        /* */
      }
    };
  }, [lassoEnabled, pickMode, lineDrawEnabled, routeDrawEnabled]);

  // 新規地点解析: 地図をクリックして候補座標を選ぶモード (pickMode 時のみ、lasso/lineDrawEnabled とは排他)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pickMode || lassoEnabled || lineDrawEnabled || routeDrawEnabled) return;
    const canvas = (() => {
      try {
        return map.getCanvas();
      } catch {
        return null;
      }
    })();
    const onClick = (e: maplibregl.MapMouseEvent) => {
      onPickLocationRef.current?.({ latitude: e.lngLat.lat, longitude: e.lngLat.lng });
    };
    map.on('click', onClick);
    if (canvas) canvas.style.cursor = 'crosshair';
    return () => {
      map.off('click', onClick);
      if (canvas) canvas.style.cursor = '';
    };
  }, [pickMode, lassoEnabled, lineDrawEnabled, routeDrawEnabled]);

  // 一括解析: 地図で線を描くモード (lineDrawEnabled 時のみ、pick/lasso とは排他)。
  // クリックで頂点を追加し (投げ縄と異なり連続ドラッグではない)、ダブルクリックまたは
  // 外部の「完了」ボタン (finishLineDrawRef 経由) で確定する。道路の曲がりを正確になぞれる
  // よう、確定前は次のクリック位置までのラバーバンド線を mousemove でプレビュー表示する。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !lineDrawEnabled || pickMode || lassoEnabled || routeDrawEnabled) return;
    const canvas = (() => {
      try {
        return map.getCanvas();
      } catch {
        return null;
      }
    })();

    let path: [number, number][] = [];
    const emptyFc = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });
    const ensureLayer = () => {
      try {
        if (!map.getSource(LINE_DRAW_SOURCE_ID)) {
          map.addSource(LINE_DRAW_SOURCE_ID, { type: 'geojson', data: emptyFc() });
          map.addLayer({
            id: LINE_DRAW_LAYER_ID,
            type: 'line',
            source: LINE_DRAW_SOURCE_ID,
            paint: { 'line-color': '#0EA5E9', 'line-width': 3 },
          });
        }
      } catch {
        /* silent */
      }
    };
    const setLineData = (coords: [number, number][]) => {
      try {
        const src = map.getSource(LINE_DRAW_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (!src) return;
        if (coords.length < 2) {
          src.setData(emptyFc());
          return;
        }
        src.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: coords },
            },
          ],
        });
      } catch {
        /* silent */
      }
    };
    const finish = () => {
      if (path.length < 2) return;
      // ダブルクリックの2回目のクリックも onClick で頂点として追加されるため、直近2点が
      // ごく近接 (2m程度) していれば片方を除去する。
      const trimmed = [...path];
      if (trimmed.length >= 2) {
        const a = trimmed[trimmed.length - 2];
        const b = trimmed[trimmed.length - 1];
        if (a && b) {
          const dLng = a[0] - b[0];
          const dLat = a[1] - b[1];
          const approxMetersPerDeg = 111_000;
          const distM = Math.hypot(dLng * approxMetersPerDeg, dLat * approxMetersPerDeg);
          if (distM < 2) trimmed.pop();
        }
      }
      if (trimmed.length < 2) {
        path = [];
        setLineData([]);
        setDrawVertexCount(0);
        return;
      }
      onLineDrawnRef.current?.(trimmed.map(([lng, lat]) => ({ latitude: lat, longitude: lng })));
      path = [];
      setLineData([]);
      setDrawVertexCount(0);
    };
    finishLineDrawRef.current = finish;

    const onClick = (e: maplibregl.MapMouseEvent) => {
      ensureLayer();
      path.push([e.lngLat.lng, e.lngLat.lat]);
      setLineData(path);
      setDrawVertexCount(path.length);
    };
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (path.length === 0) return;
      setLineData([...path, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const onDblClick = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault();
      finish();
    };

    map.on('click', onClick);
    map.on('mousemove', onMouseMove);
    map.on('dblclick', onDblClick);
    try {
      map.doubleClickZoom.disable();
    } catch {
      /* */
    }
    if (canvas) canvas.style.cursor = 'crosshair';

    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMouseMove);
      map.off('dblclick', onDblClick);
      finishLineDrawRef.current = null;
      path = [];
      setDrawVertexCount(0);
      if (canvas) canvas.style.cursor = '';
      try {
        map.doubleClickZoom.enable();
      } catch {
        /* */
      }
      try {
        if (map.getLayer(LINE_DRAW_LAYER_ID)) map.removeLayer(LINE_DRAW_LAYER_ID);
        if (map.getSource(LINE_DRAW_SOURCE_ID)) map.removeSource(LINE_DRAW_SOURCE_ID);
      } catch {
        /* */
      }
    };
  }, [lineDrawEnabled, pickMode, lassoEnabled, routeDrawEnabled]);

  // 「既存地点から路線を作成」モード (routeDrawEnabled 時のみ、他モードとは排他)。UIの実装は
  // 上の一括解析用線描画と全く同じ骨格を踏襲する (docs/25 §10-6 拡張、2026-07-21) — 唯一の違いは
  // 完了時に新規AI解析を起動せず、描いた線の座標だけを呼出元 (`onRouteDrawn`) に渡す点。
  // 既存点の近接判定・postIds算出は呼出元 (`RouteReportModal.tsx`) の責務とし、地図コンポーネント
  // に敷居値等のビジネスロジックを持ち込まない。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeDrawEnabled || pickMode || lassoEnabled || lineDrawEnabled) return;
    const canvas = (() => {
      try {
        return map.getCanvas();
      } catch {
        return null;
      }
    })();

    let path: [number, number][] = [];
    const emptyFc = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });
    const ensureLayer = () => {
      try {
        if (!map.getSource(ROUTE_DRAW_SOURCE_ID)) {
          map.addSource(ROUTE_DRAW_SOURCE_ID, { type: 'geojson', data: emptyFc() });
          map.addLayer({
            id: ROUTE_DRAW_LAYER_ID,
            type: 'line',
            source: ROUTE_DRAW_SOURCE_ID,
            paint: { 'line-color': '#0EA5E9', 'line-width': 3 },
          });
        }
      } catch {
        /* silent */
      }
    };
    const setLineData = (coords: [number, number][]) => {
      try {
        const src = map.getSource(ROUTE_DRAW_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (!src) return;
        if (coords.length < 2) {
          src.setData(emptyFc());
          return;
        }
        src.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: coords },
            },
          ],
        });
      } catch {
        /* silent */
      }
    };
    const finish = () => {
      if (path.length < 2) return;
      // ダブルクリックの2回目のクリックも onClick で頂点として追加されるため、直近2点が
      // ごく近接 (2m程度) していれば片方を除去する。
      const trimmed = [...path];
      if (trimmed.length >= 2) {
        const a = trimmed[trimmed.length - 2];
        const b = trimmed[trimmed.length - 1];
        if (a && b) {
          const dLng = a[0] - b[0];
          const dLat = a[1] - b[1];
          const approxMetersPerDeg = 111_000;
          const distM = Math.hypot(dLng * approxMetersPerDeg, dLat * approxMetersPerDeg);
          if (distM < 2) trimmed.pop();
        }
      }
      if (trimmed.length < 2) {
        path = [];
        setLineData([]);
        setRouteDrawVertexCount(0);
        return;
      }
      onRouteDrawnRef.current?.(trimmed.map(([lng, lat]) => ({ latitude: lat, longitude: lng })));
      path = [];
      setLineData([]);
      setRouteDrawVertexCount(0);
    };
    finishRouteDrawRef.current = finish;

    const onClick = (e: maplibregl.MapMouseEvent) => {
      ensureLayer();
      path.push([e.lngLat.lng, e.lngLat.lat]);
      setLineData(path);
      setRouteDrawVertexCount(path.length);
    };
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (path.length === 0) return;
      setLineData([...path, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const onDblClick = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault();
      finish();
    };

    map.on('click', onClick);
    map.on('mousemove', onMouseMove);
    map.on('dblclick', onDblClick);
    try {
      map.doubleClickZoom.disable();
    } catch {
      /* */
    }
    if (canvas) canvas.style.cursor = 'crosshair';

    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMouseMove);
      map.off('dblclick', onDblClick);
      finishRouteDrawRef.current = null;
      path = [];
      setRouteDrawVertexCount(0);
      if (canvas) canvas.style.cursor = '';
      try {
        map.doubleClickZoom.enable();
      } catch {
        /* */
      }
      try {
        if (map.getLayer(ROUTE_DRAW_LAYER_ID)) map.removeLayer(ROUTE_DRAW_LAYER_ID);
        if (map.getSource(ROUTE_DRAW_SOURCE_ID)) map.removeSource(ROUTE_DRAW_SOURCE_ID);
      } catch {
        /* */
      }
    };
  }, [routeDrawEnabled, pickMode, lassoEnabled, lineDrawEnabled]);

  // 新規地点解析の候補座標マーカー (専用スタイル、最大1個)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pendingLocation) return;
    const el = document.createElement('div');
    el.style.cssText =
      'width:28px;height:28px;border-radius:50%;background:#F59E0B;border:3px solid white;' +
      'box-shadow:0 0 0 2px #F59E0B,0 1px 4px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;' +
      'font-size:15px;color:white;font-weight:700;line-height:1;';
    el.textContent = '+';
    pendingMarkerRef.current = new maplibregl.Marker({ element: el })
      .setLngLat([pendingLocation.longitude, pendingLocation.latitude])
      .addTo(map);
    return () => {
      pendingMarkerRef.current?.remove();
      pendingMarkerRef.current = null;
    };
  }, [pendingLocation]);

  return (
    <div ref={wrapperRef} className="relative h-full">
      <div
        ref={containerRef}
        className="w-full h-full min-h-[500px] rounded-lg border border-slate-200"
        data-testid="street-survey-map"
      />
      {isFullscreen && children && (
        <div className="absolute top-3 left-3 right-16 bg-white/95 rounded-lg shadow px-3 py-2 max-w-fit">
          {children}
        </div>
      )}
      {lineDrawEnabled && drawVertexCount >= 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 rounded-full shadow px-3 py-2 flex items-center gap-2 text-sm">
          <span className="text-slate-600">{drawVertexCount}点</span>
          <Button
            size="sm"
            disabled={drawVertexCount < 2}
            onClick={() => finishLineDrawRef.current?.()}
          >
            完了
          </Button>
        </div>
      )}
      {lassoEnabled && areaVertexCount >= 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 rounded-full shadow px-3 py-2 flex items-center gap-2 text-sm">
          <span className="text-slate-600">{areaVertexCount}点</span>
          <Button
            size="sm"
            disabled={areaVertexCount < 3}
            onClick={() => finishAreaDrawRef.current?.()}
          >
            確定
          </Button>
        </div>
      )}
      {routeDrawEnabled && routeDrawVertexCount >= 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/95 rounded-full shadow px-3 py-2 flex items-center gap-2 text-sm">
          <span className="text-slate-600">{routeDrawVertexCount}点</span>
          <Button
            size="sm"
            disabled={routeDrawVertexCount < 2}
            onClick={() => finishRouteDrawRef.current?.()}
          >
            完了
          </Button>
        </div>
      )}
      {legendAxis ? (
        <div className="absolute bottom-3 left-3 bg-white/90 rounded shadow text-xs">
          <button
            type="button"
            onClick={() => setLegendOpen((v) => !v)}
            className="flex items-center gap-1.5 w-full px-3 py-2 font-semibold text-slate-600"
            aria-expanded={legendOpen}
          >
            <span>{legendAxis.label}</span>
            <span className="ml-auto text-slate-400">{legendOpen ? '▾' : '▸'}</span>
          </button>
          {legendOpen && (
            <div className="px-3 pb-2 space-y-0.5 max-h-64 overflow-y-auto">
              {/* 集計から除く行 (M2 excludeFromAggregation) は凡例に出さない (地図でも数えないため)。 */}
              {(legendAxis.axis.master?.rows ?? [])
                .filter((row) => !row.excludeFromAggregation)
                .map((row) => (
                  <div key={categoryRowKey(row)} className="flex items-center gap-1.5">
                    <span
                      className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] leading-none"
                      style={{
                        background: resolveChipColor(row, legendAxis.axis.master ?? undefined),
                      }}
                    >
                      {resolveChipEmoji(row) ?? ''}
                    </span>
                    {row.category}
                  </div>
                ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
